import React, { useEffect, useCallback, useState } from 'react';
import { Card, Button, Spin, Progress, Alert, message, Dropdown, Menu } from 'antd';
import {
  ReloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  HolderOutlined,
  DownloadOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  MoreOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { ResizableBox, ResizeCallbackData } from 'react-resizable';
import 'react-resizable/css/styles.css';
import { ApplicationState } from '../../../reducers';
import { ScorecardData, JobStatus } from '../../../../types/scorecard';
import { BlockType } from '../../../../types/receiver';
import BrandScorecardMetrics from './scorecard/BrandScorecardMetrics';
import ValidationMetrics from './scorecard/ValidationMetrics';
import PartNumberBreakdown, {
  PartNumberSearchParams,
  PartNumberPaginationInfo,
} from './scorecard/PartNumberBreakdown';
import receiverActions from '../../../actions/receiver';
import { exportScorecardStats } from '../../../actions/receiver/scorecard';

interface ScorecardCompletenessTabProps {
  receiverName?: string;
  data?: ScorecardData;
  loading?: boolean;
  onRefresh?: () => void;
  receiverId?: number;
  onFetchScorecardData?: (receiverId: number) => Promise<any>;
  onSearchPartNumbers?: (receiverId: number, params: PartNumberSearchParams) => Promise<any>;
  partNumberPagination?: PartNumberPaginationInfo;
  partNumberLoading?: boolean;
  refreshJobStatus?: JobStatus | null;
  onCheckJobStatus?: (jobId: string) => void;
}

const DEFAULT_BLOCK_ORDER: BlockType[] = ['brandMetrics', 'validation', 'partNumber'];

const ScorecardCompletenessTab: React.FC<ScorecardCompletenessTabProps> = ({
  receiverName,
  data,
  loading = false,
  onRefresh,
  receiverId,
  onFetchScorecardData,
  onSearchPartNumbers,
  partNumberPagination,
  partNumberLoading = false,
  refreshJobStatus,
  onCheckJobStatus,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  // Get block order from Redux store or use default
  const savedBlockOrder = useSelector(
    (state: ApplicationState) => state.receiver.receivers.scorecardBlockOrder
  );
  const [blockOrder, setBlockOrder] = useState<BlockType[]>(savedBlockOrder || DEFAULT_BLOCK_ORDER);
  const [draggedBlock, setDraggedBlock] = useState<BlockType | null>(null);
  const [dragOverBlock, setDragOverBlock] = useState<BlockType | null>(null);
  const [isDragOverEmpty, setIsDragOverEmpty] = useState(false);
  const [visibleBlocks, setVisibleBlocks] = useState<Set<BlockType>>(new Set(DEFAULT_BLOCK_ORDER));
  const [exportingBlock, setExportingBlock] = useState<BlockType | null>(null);
  const [blockSizes, setBlockSizes] = useState<
    Record<BlockType, { width: number; height: number }>
  >({
    brandMetrics: { width: 600, height: 400 },
    validation: { width: 600, height: 400 },
    partNumber: { width: 600, height: 400 },
  });

  const handlePartNumberSearch = useCallback(
    async (params: PartNumberSearchParams) => {
      if (!receiverId || !onSearchPartNumbers) return;
      await onSearchPartNumbers(receiverId, params);
    },
    [receiverId, onSearchPartNumbers]
  );

  // Default empty data structure
  const defaultData: ScorecardData = {
    brandMetrics: [],
    validationMetrics: [],
    partNumberValidations: [],
    totalSKUs: 0,
    lastUpdated: t('common:noData'),
  };

  const scorecardData = data || defaultData;

  useEffect(() => {
    if (receiverId && onFetchScorecardData) {
      onFetchScorecardData(receiverId);
    }
  }, [receiverId, onFetchScorecardData]);

  useEffect(() => {
    if (receiverId && onSearchPartNumbers) {
      onSearchPartNumbers(receiverId, { page: 1, limit: 50 });
    }
  }, [receiverId, onSearchPartNumbers]);

  useEffect(() => {
    if (!refreshJobStatus || !onCheckJobStatus) return;

    const shouldPoll =
      refreshJobStatus.status === 'queued' ||
      refreshJobStatus.status === 'running' ||
      refreshJobStatus.status === 'processing';
    if (!shouldPoll) return;

    const pollInterval = setInterval(() => {
      onCheckJobStatus(refreshJobStatus.jobId);
    }, 3000);

    return () => clearInterval(pollInterval);
  }, [refreshJobStatus, onCheckJobStatus]);

  // Fetch updated data when refresh job completes successfully
  useEffect(() => {
    if (!refreshJobStatus || !receiverId || !onFetchScorecardData) return;

    if (refreshJobStatus.status === 'completed') {
      setTimeout(() => {
        onFetchScorecardData(receiverId);
      }, 500);
    }
  }, [refreshJobStatus?.status, refreshJobStatus?.jobId, receiverId, onFetchScorecardData]);

  const handleRefresh = async () => {
    if (!receiverId) return;
    try {
      await onRefresh?.();
    } catch (error) {
      // Error handled by parent component
    }
  };
  const handleDragStart = useCallback((block: BlockType) => {
    setDraggedBlock(block);
  }, []);

  const handleDragOver = useCallback(
    (e: React.DragEvent, block: BlockType) => {
      e.preventDefault();
      e.stopPropagation();
      if (draggedBlock && draggedBlock !== block) {
        setDragOverBlock(block);
        setIsDragOverEmpty(false);
      }
    },
    [draggedBlock]
  );

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const relatedTarget = e.relatedTarget as HTMLElement;
    const currentTarget = e.currentTarget as HTMLElement;

    if (!currentTarget.contains(relatedTarget)) {
      setDragOverBlock(null);
    }
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent, dropBlock: BlockType) => {
      e.preventDefault();
      if (!draggedBlock || draggedBlock === dropBlock) {
        setDraggedBlock(null);
        setDragOverBlock(null);
        return;
      }

      const newOrder = [...blockOrder];
      const draggedIndex = newOrder.indexOf(draggedBlock);
      const dropIndex = newOrder.indexOf(dropBlock);

      newOrder.splice(draggedIndex, 1);
      newOrder.splice(dropIndex, 0, draggedBlock);

      setBlockOrder(newOrder);
      // Defer Redux dispatch to avoid blocking UI
      requestAnimationFrame(() => {
        dispatch(receiverActions.setScorecardBlockOrder(newOrder));
      });
      setDraggedBlock(null);
      setDragOverBlock(null);
    },
    [draggedBlock, blockOrder, dispatch]
  );

  const handleDragEnd = useCallback(() => {
    setDraggedBlock(null);
    setDragOverBlock(null);
  }, []);

  const handleExport = async (blockType: BlockType) => {
    if (!receiverId) return;

    const exportTypeMap: Record<BlockType, 'brand' | 'validation' | 'partNumber'> = {
      brandMetrics: 'brand',
      validation: 'validation',
      partNumber: 'partNumber',
    };

    try {
      setExportingBlock(blockType);
      await exportScorecardStats(receiverId, exportTypeMap[blockType], receiverName);
      message.success(t('receiver:exportSuccess'));
    } catch (error) {
      message.error(t('receiver:exportFailed'));
    } finally {
      setExportingBlock(null);
    }
  };

  const toggleBlockVisibility = (blockType: BlockType) => {
    setVisibleBlocks(prev => {
      const newSet = new Set(prev);
      if (newSet.has(blockType)) {
        newSet.delete(blockType);
      } else {
        newSet.add(blockType);
      }
      return newSet;
    });
  };

  const handleResize = useCallback(
    (blockType: BlockType, size: { width: number; height: number }) => {
      setBlockSizes(prev => ({
        ...prev,
        [blockType]: size,
      }));
    },
    []
  );

  const renderJobStatus = () => {
    if (!refreshJobStatus) return null;

    const { status, message, progress, estimatedCompletion, completedAt, error } = refreshJobStatus;

    if (status === 'queued') {
      return (
        <Alert
          message={
            <div className="flex items-center gap-2">
              <ClockCircleOutlined className="text-blue-500" />
              <span className="text-sm">{t('receiver:refreshQueued')}</span>
            </div>
          }
          description={
            estimatedCompletion && (
              <div className="text-xs text-gray-600">
                {t('receiver:estimatedCompletion')}:{' '}
                {new Date(estimatedCompletion).toLocaleTimeString()}
              </div>
            )
          }
          type="info"
          showIcon={false}
          className="mb-3"
        />
      );
    }

    if (status === 'running' || status === 'processing') {
      return (
        <Alert
          message={
            <div className="flex items-center gap-2">
              <Spin size="small" />
              <span className="text-sm">{t('receiver:refreshInProgress')}</span>
            </div>
          }
          description={
            <div className="mt-2">
              {progress !== undefined && (
                <Progress
                  percent={Math.round(progress)}
                  size="small"
                  status="active"
                  className="mb-2"
                />
              )}
              <div className="text-xs text-gray-600">{message}</div>
            </div>
          }
          type="info"
          showIcon={false}
          className="mb-3"
        />
      );
    }

    if (status === 'completed') {
      return (
        <Alert
          message={
            <div className="flex items-center gap-2">
              <CheckCircleOutlined className="text-green-500" />
              <span className="text-sm">{t('receiver:refreshCompleted')}</span>
            </div>
          }
          description={
            completedAt && (
              <div className="text-xs text-gray-600">
                {t('receiver:completedAt')}: {new Date(completedAt).toLocaleTimeString()}
              </div>
            )
          }
          type="success"
          showIcon={false}
          className="mb-3"
          closable
        />
      );
    }

    if (status === 'failed') {
      return (
        <Alert
          message={
            <div className="flex items-center gap-2">
              <CloseCircleOutlined className="text-red-500" />
              <span className="text-sm">{t('receiver:refreshFailed')}</span>
            </div>
          }
          description={error && <div className="text-xs text-red-600">{error}</div>}
          type="error"
          showIcon={false}
          className="mb-3"
          closable
        />
      );
    }

    return null;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center p-8">
        <Spin size="large" />
        <div className="ml-2">{t('receiver:loadingScorecardData')}</div>
      </div>
    );
  }

  // Render block content based on type
  const renderBlockContent = (blockType: BlockType) => {
    switch (blockType) {
      case 'brandMetrics':
        return {
          title: t('receiver:brandScorecardAchievement'),
          content: <BrandScorecardMetrics data={scorecardData.brandMetrics} />,
        };
      case 'validation':
        return {
          title: t('receiver:validationImpact'),
          content: <ValidationMetrics data={scorecardData.validationMetrics} />,
        };
      case 'partNumber':
        return {
          title: t('receiver:partNumberBreakdown'),
          content: (
            <PartNumberBreakdown
              data={scorecardData.partNumberValidations}
              loading={partNumberLoading}
              pagination={partNumberPagination}
              onSearch={handlePartNumberSearch}
            />
          ),
        };
      default:
        return { title: '', content: null };
    }
  };

  return (
    <div className="h-full flex flex-col scorecard-completeness-tab">
      <div className="px-6 pt-4 flex-shrink-0">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600 }}>
              {t('receiver:scorecardCompleteness')}
            </h3>
            <div className="text-sm text-gray-600 mt-1">
              {t('receiver:scorecardSubtitle', { count: scorecardData.totalSKUs })}
            </div>
          </div>
          <div className="flex flex-col items-end gap-1">
            <div className="flex items-center gap-2">
              <Dropdown
                overlay={
                  <Menu>
                    {DEFAULT_BLOCK_ORDER.map(blockType => (
                      <Menu.Item key={blockType} onClick={() => toggleBlockVisibility(blockType)}>
                        {visibleBlocks.has(blockType) ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                        <span className="ml-2">
                          {blockType === 'brandMetrics' && t('receiver:brandScorecardAchievement')}
                          {blockType === 'validation' && t('receiver:validationImpact')}
                          {blockType === 'partNumber' && t('receiver:partNumberBreakdown')}
                        </span>
                      </Menu.Item>
                    ))}
                  </Menu>
                }
                trigger={['click']}
              >
                <Button icon={<MoreOutlined />} size="middle">
                  {t('common:toggleVisibility')}
                </Button>
              </Dropdown>
              <Button
                type="primary"
                onClick={handleRefresh}
                loading={
                  loading ||
                  refreshJobStatus?.status === 'queued' ||
                  refreshJobStatus?.status === 'running' ||
                  refreshJobStatus?.status === 'processing'
                }
                size="middle"
                icon={<ReloadOutlined />}
                disabled={
                  refreshJobStatus?.status === 'queued' ||
                  refreshJobStatus?.status === 'running' ||
                  refreshJobStatus?.status === 'processing'
                }
              >
                {t('receiver:refreshData')}
              </Button>
            </div>
            <div className="text-xs text-gray-500">
              {t('receiver:lastUpdated')} {scorecardData.lastUpdated}
            </div>
          </div>
        </div>

        {renderJobStatus()}
      </div>

      <div className="flex-1 px-6 pb-6 overflow-auto">
        <div
          className={`scorecard-grid ${isDragOverEmpty ? 'drag-over-empty' : ''}`}
          onDragOver={e => {
            e.preventDefault();
            if (draggedBlock) {
              const target = e.target as HTMLElement;
              const isEmptySpace = target.classList.contains('scorecard-grid');
              if (isEmptySpace) {
                setDragOverBlock(null);
                setIsDragOverEmpty(true);
              } else {
                setIsDragOverEmpty(false);
              }
            }
          }}
          onDragLeave={e => {
            const target = e.target as HTMLElement;
            if (target.classList.contains('scorecard-grid')) {
              setIsDragOverEmpty(false);
            }
          }}
          onDrop={e => {
            e.preventDefault();
            if (draggedBlock && !dragOverBlock) {
              const newOrder = blockOrder.filter(b => b !== draggedBlock);
              newOrder.push(draggedBlock);
              setBlockOrder(newOrder);
              // Defer Redux dispatch to avoid blocking UI
              requestAnimationFrame(() => {
                dispatch(receiverActions.setScorecardBlockOrder(newOrder));
              });
            }
            setDraggedBlock(null);
            setDragOverBlock(null);
            setIsDragOverEmpty(false);
          }}
        >
          {blockOrder
            .filter(blockType => visibleBlocks.has(blockType))
            .map(blockType => {
              const { title, content } = renderBlockContent(blockType);
              const isDragging = draggedBlock === blockType;
              const isOver = dragOverBlock === blockType;
              const isExporting = exportingBlock === blockType;
              const size = blockSizes[blockType];

              return (
                <div
                  key={blockType}
                  className={`scorecard-block ${isDragging ? 'dragging' : ''} ${
                    isOver ? 'drag-over' : ''
                  }`}
                  draggable
                  onDragStart={() => handleDragStart(blockType)}
                  onDragOver={e => handleDragOver(e, blockType)}
                  onDragLeave={e => handleDragLeave(e)}
                  onDrop={e => handleDrop(e, blockType)}
                  onDragEnd={handleDragEnd}
                >
                  <ResizableBox
                    width={size.width}
                    height={size.height}
                    minConstraints={[400, 300]}
                    maxConstraints={[1200, 800]}
                    onResize={(_e: React.SyntheticEvent, data: ResizeCallbackData) =>
                      handleResize(blockType, { width: data.size.width, height: data.size.height })
                    }
                    resizeHandles={['se']}
                    className="resizable-scorecard-block"
                  >
                    <Card
                      size="small"
                      title={
                        <div className="scorecard-block-title">
                          <HolderOutlined className="drag-icon" />
                          <span>{title}</span>
                        </div>
                      }
                      extra={
                        <Button
                          type="text"
                          size="small"
                          icon={<DownloadOutlined />}
                          loading={isExporting}
                          onClick={() => handleExport(blockType)}
                        >
                          {t('common:export')}
                        </Button>
                      }
                      className="scorecard-card"
                      style={{ height: '100%' }}
                    >
                      {content}
                    </Card>
                  </ResizableBox>
                </div>
              );
            })}
        </div>
      </div>
    </div>
  );
};

export default ScorecardCompletenessTab;
